import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";

export enum GenerationType {
  words,
  sentences,
  paragraphs,
}

export interface LoremIpsumGeneratorState {
  generationType: GenerationType;
  generationCount: number;
  generatedLoremIpsum: string;
  isLoremIpsumSnippetCopied: boolean;
}

const initialState: LoremIpsumGeneratorState = {
  generationType: GenerationType.paragraphs,
  generationCount: 3,
  generatedLoremIpsum: "Lorem Ipsum",
  isLoremIpsumSnippetCopied: false,
};

export const LoremIpsumGeneratorSlice = createSlice({
  name: "loremIpsumGenerator",
  initialState,
  reducers: {
    generationCountOnChanged(state, action: PayloadAction<number>) {
      state.generationCount = action.payload;
      state.isLoremIpsumSnippetCopied = false;
    },
    generationTypeOnChanged(state, action: PayloadAction<GenerationType>) {
      state.generationType = action.payload;
      state.isLoremIpsumSnippetCopied = false;
    },
    loremIpsumSnippetCopied(state, action: PayloadAction<boolean>) {
      state.isLoremIpsumSnippetCopied = action.payload;
    },
  },
   extraReducers: (builder) => {
      builder.addCase(generateLoremIpsumAsync.fulfilled, (state, action) => {
        if (action.payload === undefined) {
          return;
        }

        state.generatedLoremIpsum = action.payload;
        state.isLoremIpsumSnippetCopied = false;
      });
    }
});

export const generateLoremIpsumAsync = createAsyncThunk<
  string,
  void,
  { state: RootState }
>("LoremIpsumGenerator/generateLoremIpsumAsync", async (_, { getState }) => {
  const state = getState();

  const loremIpsumModule = await import("lorem-ipsum");
  const lorem = new loremIpsumModule.LoremIpsum({
    sentencesPerParagraph: {
      max: 8,
      min: 4,
    },
    wordsPerSentence: {
      max: 16,
      min: 4,
    },
  });

  switch (state.loremIpsumGenerator.generationType) {
    case GenerationType.paragraphs:
      return lorem.generateParagraphs(state.loremIpsumGenerator.generationCount);
    case GenerationType.sentences:
      return lorem.generateSentences(state.loremIpsumGenerator.generationCount);
    case GenerationType.words:
      return lorem.generateWords(state.loremIpsumGenerator.generationCount);
    default:
      return "";
  }
});

export const {
  generationCountOnChanged,
  generationTypeOnChanged,
  loremIpsumSnippetCopied,
} = LoremIpsumGeneratorSlice.actions;

export const selectState = (state: RootState) => state.loremIpsumGenerator;

export default LoremIpsumGeneratorSlice.reducer;
